<template>
  <v-container class="pt-0" fluid>
    <v-row class="align-center">
      <v-col v-if="!typeSlug" cols="auto">
        <v-card-title class="pt-0 pl-0">
          Items
        </v-card-title>
      </v-col>
      <v-spacer />
      <v-col cols="auto" style="min-width: 220px;">
        <select-project
          v-model="searchArgs.entity_id"
          label="Select project"
          @input="onProjectInput"
        />
      </v-col>
      <v-col v-if="!typeSlug" cols="auto" style="min-width: 180px;">
        <v-select
          v-model="searchArgs.type_id"
          :items="types"
          item-text="name"
          item-value="type_id"
          label="Type"
          clearable
          hide-details
          :disabled="!searchArgs.entity_id"
          @change="onTypeChange"
        />
      </v-col>
      <v-col cols="auto" style="min-width: 150px;">
        <v-select
          v-model="searchArgs.status"
          :items="statusOptions"
          label="Status"
          clearable
          hide-details
          @change="loadData"
        />
      </v-col>
      <v-col cols="auto">
        <v-btn
          color="primary"
          :loading="isLoading"
          :disabled="!searchArgs.entity_id || !searchArgs.type_id"
          @click="onAdd"
        >
          Add Item
        </v-btn>
      </v-col>
    </v-row>

    <v-row>
      <v-col cols="12" class="pt-0">
        <v-card>
          <v-data-table
            v-model="selectedItems"
            :headers="headers"
            :items="items"
            :loading="isLoading"
            :options.sync="pagination"
            :footer-props="{ 'items-per-page-options': [ 25, 50, 100 ] }"
            :server-items-length="total"
            item-key="item_id"
            show-select
            fixed-header
            :height="tableHeight"
          >
            <template #top>
              <v-toolbar v-if="selectedItems.length" flat dense color="blue-grey lighten-5">
                <span class="subtitle-2">{{ selectedItems.length }} item(s) selected</span>
                <v-spacer />
                <v-btn small color="primary" @click="copyDialogOpen = true">
                  Copy to...
                </v-btn>
              </v-toolbar>
            </template>
            <template #item.type_id="{ value }">
              {{ typeNameById[value] || value }}
            </template>
            <template #item.status="{ value }">
              <v-chip :color="statusColor[value] || 'grey'" small outlined>
                {{ value }}
              </v-chip>
            </template>
            <template #item.is_changed="{ value }">
              <v-icon v-if="value" small color="warning">mdi-alert-circle</v-icon>
            </template>
            <template #item.actions="{ item }">
              <v-icon class="mr-2" @click="onEdit(item)">
                mdi-pencil
              </v-icon>
              <v-icon class="mr-2" @click="onShowVersions(item)">
                mdi-history
              </v-icon>
              <v-icon color="error" @click="onDelete(item)">
                mdi-delete
              </v-icon>
            </template>
          </v-data-table>
        </v-card>
      </v-col>
    </v-row>

    <drawer-dialog
      v-model="isEditing"
      width="70%"
      max-width="1200px"
    >
      <item-form
        v-if="isEditing"
        :item="editingTarget"
        :types="types"
        :is-loading="isLoading"
        @close="isEditing = false"
        @save="onSave"
      />
    </drawer-dialog>

    <drawer-dialog
      v-model="isVersionsOpen"
      width="60%"
      max-width="1000px"
    >
      <item-versions
        v-if="isVersionsOpen"
        :item="versionsTarget"
        :is-loading="isLoading"
        @close="isVersionsOpen = false"
        @rollback="onRollbackComplete"
      />
    </drawer-dialog>

    <!-- Copy Items Dialog -->
    <v-dialog v-model="copyDialogOpen" max-width="500">
      <v-card>
        <v-card-title>Copy {{ selectedItems.length }} item(s) to another project</v-card-title>
        <v-divider />
        <v-card-text class="pt-4">
          <select-project
            v-model="copyTargetEntityId"
            label="Target project"
          />
          <v-alert v-if="copyTargetEntityId" type="warning" dense text class="mt-3 mb-0">
            Items will be copied as drafts. Categories may not exist in the target project.
          </v-alert>
        </v-card-text>
        <v-divider />
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="copyDialogOpen = false">Cancel</v-btn>
          <v-btn color="primary" :disabled="!copyTargetEntityId" :loading="isCopying" @click="onCopyItems">
            Copy
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { API } from '@/utils/api'
import { STATUS_OPTIONS } from '@/components/cms/constants'
import { DrawerDialog } from '@/components/core/DrawerDialog'
import SelectProject from '@/components/core/SelectProject/SelectProject'
import ItemForm from './item-form/ItemForm.vue'
import ItemVersions from './ItemVersions'
export default {
  components: {
    DrawerDialog,
    SelectProject,
    ItemForm,
    ItemVersions,
  },
  props: {
    typeSlug: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      isLoading: false,
      items: [],
      types: [],
      total: 0,
      editingTarget: null,
      versionsTarget: null,
      selectedItems: [],
      copyDialogOpen: false,
      copyTargetEntityId: null,
      isCopying: false,
      searchArgs: {
        entity_id: null,
        type_id: null,
        status: null,
      },
      pagination: {
        itemsPerPage: 25,
        page: 1,
      },
      statusOptions: STATUS_OPTIONS,
      statusColor: {
        draft: 'orange',
        published: 'primary',
        archived: 'grey',
      },
      headers: [
        { text: 'ID', value: 'item_id', width: 80 },
        { text: 'Name', value: 'name' },
        { text: 'Slug', value: 'slug' },
        { text: 'Type', value: 'type_id', width: 120 },
        { text: 'Status', value: 'status', width: 120 },
        { text: 'Created', value: 'created' },
        { text: 'Changed', value: 'is_changed', width: 90 },
        { text: 'Updated', value: 'updated' },
        { text: 'Actions', value: 'actions', sortable: false, width: 150 },
      ],
    }
  },
  computed: {
    tableHeight() {
      // bulk-action toolbar (#top) appears when items are selected — reserve extra space
      return this.selectedItems.length ? 'calc(100vh - 300px)' : 'calc(100vh - 251px)'
    },
    isEditing: {
      get() {
        return !!this.editingTarget
      },
      set(value) {
        if (!value) {
          this.editingTarget = null
        }
      },
    },
    isVersionsOpen: {
      get() {
        return !!this.versionsTarget
      },
      set(value) {
        if (!value) {
          this.versionsTarget = null
        }
      },
    },
    typeNameById() {
      const map = {}
      for (const t of this.types) {
        map[t.type_id] = t.name
      }
      return map
    },
  },
  watch: {
    pagination: {
      deep: true,
      handler(after, before) {
        if (after.itemsPerPage !== before.itemsPerPage || after.page !== before.page) {
          this.loadData()
        }
      },
    },
    copyDialogOpen(val) {
      if (val) {
        this.copyTargetEntityId = null
      }
    },
    typeSlug(slug) {
      if (slug) {
        this.applyTypeSlugFilter(slug)
      }
    },
  },
  mounted() {
    // Don't load data here — SelectProject will trigger onProjectInput
  },
  methods: {
    onProjectInput(val) {
      if (!val) {
        return
      }
      if (this.typeSlug && !this.types.length) {
        // Load types first, then apply slug filter (which will call loadData)
        this.loadTypes().then(() => {
          this.applyTypeSlugFilter(this.typeSlug)
        })
      } else {
        this.loadTypes()
        this.loadData()
      }
    },
    loadTypes() {
      return API.call('/cms/type/list.json', {}).then(r => {
        const all = r.data.items || []
        this.types = all.filter(t => t.kind !== 'fragment')
      })
    },
    loadData() {
      if (!this.searchArgs.entity_id) {
        return
      }
      this.isLoading = true
      return API.call('/cms/item/list.json', {
        entity_id: this.searchArgs.entity_id,
        type_id: this.searchArgs.type_id,
        status: this.searchArgs.status,
        page: this.pagination.page,
        perpage: this.pagination.itemsPerPage,
      })
        .then(r => {
          this.items = r.data.items || []
          this.total = r.data.pagination ? r.data.pagination.total : this.items.length
          this.selectedItems = []
        })
        .finally(() => this.isLoading = false)
    },
    onAdd() {
      this.editingTarget = {
        entity_id: this.searchArgs.entity_id,
        type_id: this.searchArgs.type_id,
        name: '',
        slug: '',
        status: 'draft',
        category_ids: [],
        data: '{}',
        environments: ["dev"],
      }
    },
    onEdit(item) {
      this.isLoading = true
      API.call('/cms/item/get.json', { entity_id: this.searchArgs.entity_id, item_id: item.item_id }).then(r => {
        const data = r.data || {}
        const fullItem = data.item || data
        fullItem._schema = data.schema || null
        fullItem._schema_version = data.schema_version || null
        fullItem._locales = data.locales || []
        this.editingTarget = fullItem
      }).catch(() => {
        this.editingTarget = { ...item }
      }).finally(() => {
        this.isLoading = false
      })
    },
    onShowVersions(item) {
      this.versionsTarget = { ...item }
    },
    onRollbackComplete() {
      this.versionsTarget = null
      this.loadData()
    },
    onSave(item) {
      this.isLoading = true
      return API.call('/cms/item/upsert.json', item)
        .then(() => {
          this.$showSuccess('Item saved')
          this.editingTarget = null
          this.loadData()
        })
        .finally(() => this.isLoading = false)
    },
    onTypeChange() {
      this.updateRouteSlug()
      this.loadData()
    },
    updateRouteSlug() {
      const type = this.types.find(t => t.type_id === this.searchArgs.type_id)
      const slug = type ? type.slug : undefined
      const currentSlug = this.$route.params.typeSlug || undefined
      if (slug && slug !== currentSlug) {
        this.$router.replace({ name: 'CMSType', params: { typeSlug: slug, tab: 'items' } }).catch(() => {})
      }
    },
    applyTypeSlugFilter(slug) {
      if (!this.types.length) {
        return
      }
      const type = this.types.find(t => t.slug === slug)
      if (type) {
        this.searchArgs.type_id = type.type_id
      }
      this.loadData()
    },
    onCopyItems() {
      if (!this.selectedItems.length || !this.copyTargetEntityId) {
        return
      }
      this.isCopying = true
      const itemIds = this.selectedItems.map(i => i.item_id)
      API.call('/cms/item/copy.json', {
        item_ids: itemIds,
        target_entity_id: this.copyTargetEntityId,
        entity_ids: [this.searchArgs.entity_id],
      }).then(r => {
        const copied = (r.data && r.data.items) ? r.data.items.length : itemIds.length
        this.$showSuccess(`${copied} item(s) copied successfully`)
        this.copyDialogOpen = false
        this.selectedItems = []
        this.copyTargetEntityId = null
      }).finally(() => {
        this.isCopying = false
      })
    },
    onDelete(item) {
      this.$root.$confirm('Confirm', `Delete item "${item.slug}"?`)
        .then(yes => {
          if (!yes) {
            return
          }
          this.isLoading = true
          API.call('/cms/item/delete.json', { entity_id: this.searchArgs.entity_id, item_id: item.item_id })
            .then(() => {
              this.$showSuccess('Item deleted')
              this.loadData()
            })
            .finally(() => this.isLoading = false)
        })
    },
  },
}
</script>
